import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { BudgetAllowance } from '@frontend/budget/types';
import { CertificationClient } from '@frontend/certificate/api';
import { Certification } from '@frontend/certificate/types';
import { EntityType, Locale } from '@frontend/common';
import { isCostCenterUseEnforced } from '@frontend/cost-center/api';
import { EquipmentClient } from '@frontend/equipment/api';
import { IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { useLocale } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { PackageType } from '@frontend/package/types';
import { ProductClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { Stock } from '@frontend/stock/types';
import { TransactionActorClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    product?: IProduct;
    equipment?: IEquipment | null;
    kitItems: ConfirmShopKitItem[];
    usedSlots: Slot[] | null;
    submitted: boolean;
    setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    isLoading: boolean;
    handleSubmit: () => void;
    onBack: () => void;
    locale: Locale | undefined;
    isCostCenterRequired: boolean;
    selectedCostCenterId: string | null;
    selectedCostCenterName: string | null;
    hasCertification: boolean;
    hasWorkflowConfigurationError: boolean;
}

interface ConfirmShopKitItem {
    product: IProduct;
    quantity: number;
}

const useConfirmShopTransaction = (): ViewProps => {
    const { locale } = useLocale();
    const { store, onSubmit, navigationState, onBack, wizardData } = useScreen();
    const { userOverwrite } = wizardData;
    const { functionalities } = useLicenseRepository();
    const [submitted, setSubmitted] = useState<boolean>(false);
    const product = store?.data.selectedProduct as IProduct | undefined;
    const selectedAmount = store?.data?.selectedAmount ?? 1;
    const [equipment, setEquipment] = useState<IEquipment | null>(null);
    const [productStocks, setProductStocks] = useState<Stock[]>([]);
    const [kitItems, setKitItems] = useState<ConfirmShopKitItem[]>([]);
    const [availableSlots, setAvailableSlots] = useState<Slot[] | null>(null);
    const usedSlots = useMemo(() => {
        if (!availableSlots) return null;
        return availableSlots.slice(0, selectedAmount);
    }, [availableSlots, selectedAmount]);

    const [selectedAllowance, setSelectedAllowance] = useState<BudgetAllowance | null>(null);
    const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);
    const [isLoadingCertification, setIsLoadingCertification] = useState<boolean>(false);
    const [workflowInfo, setWorkflowInfo] = useState<{
        transactionWf: string;
        pickWf: string;
    } | null>(null);
    const [hasWorkflowConfigurationError, setHasWorkflowConfigurationError] = useState(false);
    const isCostCenterRequired = isCostCenterUseEnforced(navigationState.spot?.settings?.[SettingKey.ENFORCE_COST_CENTER_USE]) && !!navigationState.user?.auth;
    const selectedCostCenterId = (wizardData.selectedCostCenterId as string | null | undefined) ?? null;
    const selectedCostCenterName = (wizardData.selectedCostCenterName as string | null | undefined) ?? null;
    const transactionContext = useWizardTransaction({
        store: store,
        wizardId: store?.wizard.id
    });
    const { packages, setPackages, syncPackages } = useWizardPackages({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });
    const hasPreparedTransactionPackages = !!store?.data.transaction && packages.length > 0;
    const hasCertification = product?.type !== ProductType.EQUIPMENT || selectedCertification?.certified === true;

    useEffect(() => {
        let cancelled = false;
        setWorkflowInfo(null);
        setHasWorkflowConfigurationError(false);

        if (!product || product.type === ProductType.KIT || hasPreparedTransactionPackages) return;

        const application = product.type === ProductType.EQUIPMENT ? Application.LENDING : Application.VENDING;
        const relevantFunctionality = functionalities.find((f) => f.application === application && f.feature === Feature.OUTBOUND_TRANSACTION);
        if (!relevantFunctionality) {
            Logger.warn('Missing outbound transaction functionality for shop fulfillment', { application, productId: product.id });
            setHasWorkflowConfigurationError(true);
            return;
        }

        SettingClient.fetchSettings({ entity_type: EntityType.FUNCTIONALITY, entity_id: relevantFunctionality.id })
            .then((result) => {
                const transactionWf = result.results.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value;
                const pickWf = result.results.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value;
                if (typeof transactionWf !== 'string' || typeof pickWf !== 'string') {
                    Logger.warn('Missing workflow settings for shop fulfillment', {
                        application,
                        functionalityId: relevantFunctionality.id,
                        productId: product.id
                    });
                    if (!cancelled) setHasWorkflowConfigurationError(true);
                    return;
                }
                if (!cancelled) setWorkflowInfo({ transactionWf, pickWf });
            })
            .catch((error) => {
                Logger.error('Failed to load workflow settings for shop fulfillment', error);
                if (!cancelled) setHasWorkflowConfigurationError(true);
            });

        return () => {
            cancelled = true;
        };
    }, [functionalities, hasPreparedTransactionPackages, product]);

    useEffect(() => {
        if (product && product.type === ProductType.EQUIPMENT) {
            EquipmentClient.fetchEquipments({ product_id: product.id, account_id: product.account_id }).then((res) => {
                if (res.results.length === 0) return;
                setEquipment(res.results[0]);
            });
        }
    }, [product]);

    useEffect(() => {
        if (!product || product.type !== ProductType.EQUIPMENT || !navigationState.user) {
            setSelectedCertification(null);
            setIsLoadingCertification(false);
            return;
        }

        const cachedCertification = store?.data?.selectedCertification as Certification | null | undefined;
        if (cachedCertification) {
            setSelectedCertification(cachedCertification);
            setIsLoadingCertification(false);
            return;
        }

        setSelectedCertification(null);
        setIsLoadingCertification(true);
        DriverAuthenticationManager.getInstance()
            .waitForToken()
            .then((iotToken) =>
                CertificationClient.postGetProductCertification(
                    navigationState.user!.accountId,
                    userOverwrite?.id ?? navigationState.user!.id,
                    [product.id],
                    iotToken
                )
            )
            .then((certifications) => setSelectedCertification(certifications[product.id] ?? null))
            .catch(() => setSelectedCertification(null))
            .finally(() => setIsLoadingCertification(false));
    }, [product, navigationState.user, userOverwrite?.id, store?.data?.selectedCertification]);

    useEffect(() => {
        if (store?.data?.selectedProductStocks && navigationState.iot?.account_id) {
            const stocksData = store.data.selectedProductStocks as Stock[];
            setProductStocks(stocksData);

            SlotClient.resolveSlots(stocksData.map((stock) => stock.slot_id)).then((result) => setAvailableSlots(result.results));
        }
        if (store?.data?.selectedAllowance) {
            setSelectedAllowance(store.data.selectedAllowance as BudgetAllowance);
        }
    }, [store, navigationState.iot, navigationState.user?.auth, product, userOverwrite]);

    useEffect(() => {
        if (product?.type !== ProductType.KIT || packages.length === 0) {
            setKitItems((current) => (current.length === 0 ? current : []));
            return;
        }

        let cancelled = false;
        const resolveKitItems = async () => {
            const productIds = [...new Set(packages.map((pkg) => pkg.product_id).filter((id): id is string => !!id))];
            if (productIds.length === 0) {
                setKitItems((current) => (current.length === 0 ? current : []));
                return;
            }

            try {
                const response = await ProductClient.resolveProducts(productIds);
                if (cancelled) return;

                const quantityByProductId = new Map<string, number>();
                packages.forEach((pkg) => {
                    if (!pkg.product_id) return;
                    quantityByProductId.set(pkg.product_id, (quantityByProductId.get(pkg.product_id) ?? 0) + (pkg.quantity ?? 1));
                });

                const productById = new Map(response.results.map((resolvedProduct) => [resolvedProduct.id, resolvedProduct]));
                setKitItems(
                    productIds
                        .map((productId) => {
                            const resolvedProduct = productById.get(productId);
                            if (!resolvedProduct) return null;
                            return {
                                product: resolvedProduct,
                                quantity: quantityByProductId.get(productId) ?? 1
                            };
                        })
                        .filter((item): item is ConfirmShopKitItem => item !== null)
                );
            } catch (error) {
                Logger.error('Failed to resolve kit items:', error);
                if (!cancelled) setKitItems([]);
            }
        };

        resolveKitItems();

        return () => {
            cancelled = true;
        };
    }, [packages, product?.type]);

    const finalizePreparedTransactionPackages = async () => {
        const transaction = await transactionContext.syncTransaction();

        await setPackages(
            packages.map((pkg) => ({
                ...pkg,
                account_id: transaction.account_id,
                transaction_id: transaction.id,
                user_id: pkg.user_id ?? transaction.user_id,
                cost_center_id: selectedCostCenterId ?? undefined
            }))
        );
        await syncPackages();

        await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
            trigger: TransactionTriggerName.TRANSACTION_PROCESS,
            location: navigationState.spot?.id ?? undefined,
            actor: navigationState.user?.id ?? undefined
        });
    };

    const handleSubmit = async () => {
        if (!store || !product) return;
        if (hasWorkflowConfigurationError) return;
        if (!hasPreparedTransactionPackages && (!workflowInfo || !usedSlots)) return;
        if (!hasCertification) return;

        setSubmitted(true);
        try {
            if (hasPreparedTransactionPackages) {
                await finalizePreparedTransactionPackages();
                onSubmit();
                return;
            }

            if (!workflowInfo || !usedSlots) return;

            const selectedWorkflowInfo = workflowInfo;
            const selectedUsedSlots = usedSlots;

            const budgetIds = distributeBudgets(selectedAmount, selectedAllowance);
            const transaction = await transactionContext.syncTransaction({
                application: product.type === ProductType.EQUIPMENT ? Application.LENDING : Application.VENDING,
                type: product.type === ProductType.EQUIPMENT ? TransactionType.LENDING : TransactionType.VENDING,
                workflow_id: selectedWorkflowInfo.transactionWf,
                spot_id: navigationState.iot!.spot_id,
                user_id: userOverwrite?.id ?? navigationState.user!.id,
                iot_id: navigationState.iot!.id,
                state: TransactionStateName.TRANSACTION_CREATED
            });

            TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                type: TransactionActorType.PICKED_UP_BY,
                user_id: userOverwrite?.id || navigationState.user?.id
            });
            if (userOverwrite) {
                TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.INITIATED_BY,
                    user_id: navigationState.user?.id
                });
            }

            const newPackages = productStocks.slice(0, selectedAmount).map(async (stock, index) => {
                const slot = selectedUsedSlots.find((s) => s.id === stock.slot_id);
                return await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    application: product.type === ProductType.EQUIPMENT ? Application.LENDING : Application.VENDING,
                    feature: Feature.OUTBOUND_TRANSACTION,
                    workflow_id: selectedWorkflowInfo.pickWf,
                    user_id: transaction!.user_id,
                    quantity: 1,
                    type: PackageType.PICK_UP,
                    spot_id: slot?.spot_id ?? stock.spot_id,
                    module_id: slot?.module_id ?? stock.module_id,
                    slot_id: slot?.id ?? stock.slot_id,
                    product_id: product.id,
                    equipment_id: stock.equipment_id,
                    budget_id: budgetIds[index],
                    priority: index + 1,
                    blocking: false,
                    cost_center_id: selectedCostCenterId ?? undefined
                });
            });

            const createdPackages = await Promise.all(newPackages);
            await setPackages(createdPackages);

            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id!, transaction.id!, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            onSubmit();
        } catch (error) {
            Logger.error('Error during transaction submission:', error);
            setSubmitted(false);
        }
        return;
    };

    return {
        product,
        equipment,
        kitItems,
        usedSlots,
        isLoading:
            isLoadingCertification ||
            (!hasWorkflowConfigurationError &&
                !hasPreparedTransactionPackages &&
                product?.type !== ProductType.KIT &&
                (usedSlots === null || workflowInfo === null)),
        submitted,
        setSubmitted,
        handleSubmit,
        onBack,
        locale,
        isCostCenterRequired,
        selectedCostCenterId,
        selectedCostCenterName,
        hasCertification,
        hasWorkflowConfigurationError
    };
};

export default useConfirmShopTransaction;

function distributeBudgets(amount: number, allowance: BudgetAllowance | null): (string | undefined)[] {
    if (!allowance?.budgets) {
        // No budget information available, return undefined for all packages
        return Array(amount).fill(undefined);
    }

    const budgetIds: (string | undefined)[] = [];
    const budgetEntries = Object.entries(allowance.budgets);

    // Sort budgets by quantity (optional: could prioritize certain budgets)
    // For now, just iterate through them in order
    let remaining = amount;

    for (const [budgetId, quantity] of budgetEntries) {
        if (remaining <= 0) break;

        const toAssign = Math.min(quantity, remaining);
        for (let i = 0; i < toAssign; i++) {
            budgetIds.push(budgetId);
        }
        remaining -= toAssign;
    }

    // If we still have remaining packages without budgets (shouldn't happen if allowance is correct)
    while (budgetIds.length < amount) {
        budgetIds.push(undefined);
    }

    return budgetIds;
}
